customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event); }, 300).bind(this));
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }
      this.addEventListener('click', (event) => {
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          document.activeElement.blur();
          setTimeout(() => { document.activeElement.blur(); }, 500);
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }
      if (newSearchTerm.length === 0) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
      this.searchTerm = newSearchTerm;
      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();
      if (window.visualViewport) {
        setTimeout(() => { document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`); }, 100);
        setTimeout(() => { document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`); }, 500);
      }
      if (!currentSearchTerm.length) return;
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();
      switch (event.code) {
        case 'ArrowUp': this.switchOption('up'); break;
        case 'ArrowDown': this.switchOption('down'); break;
        case 'Enter': this.selectOption(); break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allVisibleElements = Array.from(
        this.querySelectorAll('li, button.predictive-search__item')
      ).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;
      if (moveUp && !selectedElement) return;
      let selectedElementIndex = -1;
      let i = 0;
      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
        i++;
      }
      this.statusElement.textContent = '';
      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }
      if (activeElementIndex === selectedElementIndex) return;
      const activeElement = allVisibleElements[activeElementIndex];
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    // ── Breed Profile Search — client-side from embedded index ──────────
    async fetchBreedResults(searchTerm) {
      const index = window.BPH_BREED_INDEX;
      if (!index || !index.length) return [];
      const term = searchTerm.toLowerCase();
      return index
        .filter(b => b.name && b.name.toLowerCase().includes(term))
        .slice(0, 5);
    }

    buildBreedResultsHTML(breeds) {
      if (!breeds.length) return '';
      const items = breeds.map((b) => {
        const img = b.img
          ? `<img src="${b.img}" alt="${b.name}" width="40" height="40" style="width:40px;height:40px;object-fit:cover;border-radius:6px;flex-shrink:0;">`
          : `<span style="width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-size:1.4rem;flex-shrink:0;">🐾</span>`;
        return `
          <li class="predictive-search__item predictive-search__item--link" role="option" aria-selected="false">
            <a href="${b.url}" style="display:flex;align-items:center;gap:12px;padding:10px 16px;text-decoration:none;color:inherit;">
              ${img}
              <div style="min-width:0;">
                <p style="margin:0;font-weight:600;font-size:0.95rem;">${b.name}</p>
                <p style="margin:0;font-size:0.8rem;opacity:0.65;">${b.species} · Breed Profile</p>
              </div>
            </a>
          </li>`;
      }).join('');

      return `
        <div id="predictive-search-breed-profiles" style="border-top:1px solid rgba(0,0,0,0.08);padding:8px 0 4px;">
          <p style="margin:0;padding:4px 16px 6px;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;opacity:0.5;">Breed Profiles</p>
          <ul role="listbox" style="list-style:none;margin:0;padding:0;">${items}</ul>
        </div>`;
    }
    // ───────────────────────────────────────────────────────────────────

    getSearchResults(searchTerm) {
      const formData = new FormData(
        document.querySelector('.predictive_search_form') ||
        document.querySelector('.predictive_search_page_form')
      );
      const params = new URLSearchParams(formData);

      if (this.productTypes && this.productTypes.value.length > 0) {
        searchTerm = `product_type:${this.productTypes.value} AND ${searchTerm}`;
      }

      params.set('q', searchTerm);
      const urlParams = params.toString();
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      const shopifyFetch = this.cachedResults[queryKey]
        ? Promise.resolve(this.cachedResults[queryKey])
        : fetch(`${routes.predictive_search_url}?${urlParams}`, {
            signal: this.abortController.signal,
          })
            .then((response) => {
              if (!response.ok) {
                var error = new Error(response.status);
                this.close();
                throw error;
              }
              return response.text();
            })
            .then((text) => {
              const resultsMarkup = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search').innerHTML;
              this.allPredictiveSearchInstances.forEach((instance) => {
                instance.cachedResults[queryKey] = resultsMarkup;
              });
              return resultsMarkup;
            });

      const breedFetch = this.fetchBreedResults(this.searchTerm);

      Promise.all([shopifyFetch, breedFetch])
        .then(([resultsMarkup, breedMatches]) => {
          this.renderSearchResults(resultsMarkup, breedMatches);
        })
        .catch((error) => {
          if (error?.code === 20) return;
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
    }

    renderSearchResults(resultsMarkup, breedMatches = []) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      // Remove any stale breed results block first
      this.predictiveSearchResults.querySelector('#predictive-search-breed-profiles')?.remove();

      // Inject breed profile results if any
      if (breedMatches.length) {
        const breedHTML = this.buildBreedResultsHTML(breedMatches);
        const wrapper = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
        if (wrapper) {
          wrapper.insertAdjacentHTML('beforeend', breedHTML);
        } else {
          this.predictiveSearchResults.insertAdjacentHTML('beforeend', breedHTML);
        }
      }

      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(
        this.querySelector('[data-predictive-search-live-region-count-value]').textContent
      );
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) this.input.value = '';
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
